'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const getClienteCatalogRedirect = async (userId: string): Promise<string> => {
    try {
      const supabase = createClient();
      // Fetch the client's assigned catalog via the RPC (SECURITY DEFINER, no RLS issues)
      const { data: catalogData } = await supabase.rpc('get_my_catalog_access');
      const catalogs: string[] = catalogData || [];

      if (catalogs.includes('TC') && !catalogs.includes('RX')) {
        // TC-only client → redirect directly to TC catalog
        return '/studies-catalog-management?catalog=TC';
      } else if (catalogs.includes('RX') && !catalogs.includes('TC')) {
        // RX-only client → redirect directly to RX catalog
        return '/studies-catalog-management?catalog=RX';
      } else if (catalogs.length > 0) {
        // Multiple catalogs assigned → go to dashboard to choose
        return '/dashboard';
      }
    } catch {
      // Fallback on error
    }
    // No catalog assigned yet → go to dashboard
    return '/dashboard';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Ingrese su correo y contraseña');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const data = await signIn(email.trim(), password);
      // Determine redirect based on role from auth metadata
      const role =
        data?.session?.user?.app_metadata?.role ||
        data?.session?.user?.user_metadata?.role ||
        data?.user?.app_metadata?.role ||
        data?.user?.user_metadata?.role;

      const userId =
        data?.session?.user?.id ||
        data?.user?.id;

      if (role === 'super_admin') {
        // Super admin → full dashboard with all options
        router.push('/today-s-patients-panel');
      } else if (role === 'cliente') {
        // Cliente → redirect to their assigned catalog immediately
        const catalogRedirect = await getClienteCatalogRedirect(userId);
        router.push(catalogRedirect);
      } else {
        // Other roles (secretaria, technician, etc.)
        router.push('/today-s-patients-panel');
      }
      router.refresh();
    } catch (err: any) {
      setError(err?.message === 'Invalid login credentials' ? 'Correo o contraseña incorrectos' : err?.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="flex items-center gap-2.5 mb-8 lg:hidden">
          <AppLogo size={36} />
          <div>
            <span className="text-base font-700 text-slate-800">RadioReg</span>
            <p className="text-xs text-slate-400">Sistema de Admisión Radiológica</p>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-700 text-slate-800 mb-1">Bienvenido al sistema</h2>
          <p className="text-sm text-slate-500">Ingrese sus credenciales para acceder</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Correo electrónico</label>
            <div className="relative">
              <Icon name="EnvelopeIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@radiologia.ve"
                className="form-input pl-9"
                autoComplete="email"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className="form-label">Contraseña</label>
            <div className="relative">
              <Icon name="LockClosedIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="form-input pl-9 pr-10"
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5">
              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-base"
            style={{ minHeight: '48px' }}
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                <span>Verificando...</span>
              </>
            ) : (
              <>
                <Icon name="LockOpenIcon" size={16} />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Sistema de uso interno exclusivo · RadioReg v2.0
        </p>
      </div>
    </div>
  );
}